import { gql } from "@apollo/client";
import { useMenuItems } from "@resource/atlas";
import clsx from "clsx";
import { Interview } from "components/EditInterviewModal/EditInterviewModalView";
import { SkeletonCollection } from "components/Skeleton";
import {
  EditableInterviewItemCardInterviewFragment,
  EditableInterviewListInterviewFragment,
} from "generated/schemaTypes";
import { groupBy } from "lodash";
import moment from "moment-timezone";
import { ComponentPropsWithoutRef, Fragment, useMemo } from "react";

import { EditableInterviewItemCard } from "./EditableInterviewItemCard";
import { InterviewDetailsDialog } from "./InterviewDetailsDialog";
import { InterviewItemLoading } from "./InterviewItemCard";

const EDITABLE_INTERVIEW_FRAGMENT = gql`
  fragment EditableInterviewListInterviewFragment on Event {
    id
    ...EditableInterviewItemCardInterviewFragment
    ...InterviewDetailsInterviewFragment
  }
  ${EditableInterviewItemCard.fragments.interview}
  ${InterviewDetailsDialog.fragments.interview}
`;

type InterviewType = "upcoming" | "past";

const DateGroupFormat = "ddd, MMM Do";

function InterviewBreak({
  diffInHours,
  className,
}: {
  diffInHours: number;
  className?: string;
}) {
  if (diffInHours <= 0) {
    return null;
  }
  return (
    <div
      className={clsx(
        "w-full text-subtle font-semibold text-xs text-center uppercase relative",
        className
      )}
    >
      <div className="w-full h-[1px] bg-light-gray-500 absolute top-1/2" />
      <span className="bg-white relative px-2">
        {`${diffInHours}-HOUR BREAK`}
      </span>
    </div>
  );
}

function InterviewItemWithBreak({
  interview,
  previousInterview,
  menuItems,
  disableInteraction,
  onEdit,
}: {
  interview: EditableInterviewItemCardInterviewFragment;
  previousInterview?: EditableInterviewItemCardInterviewFragment;
  menuItems?:
    | ReturnType<typeof useMenuItems>
    | ((interviewId: string) => ReturnType<typeof useMenuItems>);
  disableInteraction?: boolean;
  onEdit?: (interview: Interview) => unknown;
}) {
  const diffInHours = previousInterview?.endTime
    ? moment(interview.startTime).diff(previousInterview.endTime, "hour")
    : 0;

  return (
    <Fragment key={interview.id}>
      <InterviewBreak className="my-2" diffInHours={diffInHours} />
      <EditableInterviewItemCard
        interview={interview}
        className={clsx(
          "bg-white rounded-md shadow-1 shadow-light-gray-500 p-4",
          {
            /**
             * We need a top margin if there is a previous interview and
             * if the interviews either have less than a 1 hour gap between
             * them or they're in reverse order and the diffInHours is negative.
             * The <InterviewBreak> only shows for a positive diffInHours.
             */
            "mt-4": previousInterview && diffInHours <= 0,
          },
          disableInteraction
            ? "pointer-events-none"
            : "hover:bg-light-gray-200 cursor-pointer"
        )}
        menuItems={menuItems}
        onEdit={onEdit}
      />
    </Fragment>
  );
}

function NoInterviewsMessage({ type }: { type: InterviewType }) {
  return (
    <div className="bg-light-gray-200 h-full rounded-md flex p-6 mb-2 text-dark justify-center items-center">
      <div className="flex flex-col items-center text-center">
        <p className="text-body-md text-subtle mt-2">
          No {type} interviews available
        </p>
      </div>
    </div>
  );
}

export type EditableInterviewListProps = ComponentPropsWithoutRef<"div"> & {
  interviews: EditableInterviewListInterviewFragment[] | undefined;
  onSelectInterview?(id: string): void;
  type: InterviewType;
  onEdit?: (interview: Interview) => unknown;
  menuItems?:
    | ReturnType<typeof useMenuItems>
    | ((interviewId: string) => ReturnType<typeof useMenuItems>);
  disableInteraction?: boolean;
};
export function EditableInterviewList({
  interviews,
  type,
  onSelectInterview,
  menuItems,
  disableInteraction,
  onEdit,
  ...props
}: EditableInterviewListProps) {
  const interviewsGrouped = useMemo(() => {
    const interviewsWithFormattedStartDate = (interviews || []).map(
      (interview) => {
        const startTimeWithTimezone = moment(interview.startTime).tz(
          moment.tz.guess()
        );
        const formattedStartDate =
          startTimeWithTimezone.format(DateGroupFormat);
        return {
          ...interview,
          formattedStartDate,
        };
      }
    );

    return groupBy(interviewsWithFormattedStartDate, "formattedStartDate");
  }, [interviews]);

  if (!interviews) {
    return (
      <SkeletonCollection className="flex flex-col space-y-2 w-full" count={2}>
        <InterviewItemLoading />
      </SkeletonCollection>
    );
  }

  return (
    <>
      <div
        {...props}
        className={clsx("flex flex-col gap-6 w-full", props.className)}
      >
        {interviews.length ? (
          Object.keys(interviewsGrouped).map((date) => {
            const isToday = moment(date, DateGroupFormat).isSame(
              new Date(),
              "day"
            );

            return (
              <section key={date} className="space-y-2">
                <header
                  className={clsx(
                    "uppercase text-h5",
                    type === "past" ? "text-subtle" : "text-dark"
                  )}
                >
                  {isToday ? "Today" : date}
                </header>
                <div className="flex flex-col group">
                  {interviewsGrouped[date].map((interview, interviewIndex) => (
                    <InterviewItemWithBreak
                      key={interview.id}
                      interview={interview}
                      previousInterview={
                        interviewsGrouped[date][interviewIndex - 1]
                      }
                      menuItems={menuItems}
                      disableInteraction={disableInteraction}
                      onEdit={onEdit}
                    />
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          <NoInterviewsMessage type={type} />
        )}
      </div>
    </>
  );
}

EditableInterviewList.fragments = {
  interview: EDITABLE_INTERVIEW_FRAGMENT,
};
